import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Moon, Download, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_PATH = "/resources/bedtime-prayers-for-parents.pdf";

function triggerDownload() {
  const a = document.createElement("a");
  a.href = PDF_PATH;
  a.download = "bedtime-prayers-for-parents.pdf";
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.click();
}

export default function FreeGift() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const finishUnlock = (msg) => {
    setUnlocked(true);
    toast.success(msg);
    triggerDownload();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { email: form.email.trim(), name: form.name.trim() || null });
      finishUnlock("You're in! Your free download is starting — check your inbox too. 💛");
    } catch (err) {
      if (err?.response?.status === 409) {
        finishUnlock("Welcome back! Your free download is starting. 💛");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="free-gift" className="relative z-10 py-20 md:py-28" data-testid="free-gift">
      <div className="mx-auto max-w-5xl px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-1 items-center gap-10 rounded-3xl border border-gold/25 bg-gold/[0.06] p-8 md:grid-cols-5 md:p-12"
        >
          <div className="flex justify-center md:col-span-2">
            <div className="grid h-36 w-36 place-items-center rounded-3xl bg-twilight-surface text-gold shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)]">
              <Moon className="h-16 w-16" />
            </div>
          </div>

          <div className="md:col-span-3">
            <span className="inline-block rounded-full bg-gold/15 px-3 py-1 font-body text-xs uppercase tracking-[0.2em] text-gold">
              Free Gift
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-cream md:text-4xl">
              🌙 Bedtime Prayers for Parents
            </h2>
            <p className="mt-3 font-body text-lg leading-relaxed text-slate-300">
              A beautifully designed set of simple, faith-filled bedtime prayers to share with your
              children — free. Enter your email and it's yours instantly.
            </p>

            <AnimatePresence mode="wait">
              {unlocked ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6"
                >
                  <p className="flex items-center gap-2 font-body text-gold">
                    <Check className="h-5 w-5" /> Thank you! Your download has started.
                  </p>
                  <button
                    onClick={triggerDownload}
                    data-testid="free-gift-download-again"
                    className="mt-3 inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-6 py-3 font-medium text-gold transition-[background-color] duration-300 hover:bg-gold hover:text-twilight"
                  >
                    <Download className="h-4 w-4" /> Download again
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={submit}
                  data-testid="free-gift-form"
                  className="mt-6 space-y-3"
                >
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="First name (optional)"
                    data-testid="free-gift-name"
                    className="w-full rounded-full border border-white/15 bg-white/5 px-5 py-3 text-cream placeholder:text-slate-500 outline-none transition-[border-color] duration-300 focus:border-gold"
                  />
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="your@email.com"
                      data-testid="free-gift-email"
                      className="flex-1 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-cream placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:shadow-[0_0_15px_rgba(251,191,36,0.3)]"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="free-gift-submit"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3 font-medium text-twilight transition-[background-color,box-shadow] duration-300 hover:bg-gold-light hover:shadow-[0_0_20px_rgba(251,191,36,0.5)] disabled:opacity-70"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      Get the Free PDF
                    </button>
                  </div>
                  <p className="font-body text-xs text-slate-500">No spam, ever. Unsubscribe anytime.</p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
